/**
 * Mapbox `Marker` adapter — wraps `mapboxgl.Marker` to match the connector
 * surface. Mounts a marker when the consuming context's map is ready, removes
 * on unmount, syncs `position` changes via `setLngLat`. Drag callbacks fire
 * with the new lat/lng so the consumer doesn't need a `mapboxgl` import.
 *
 * Hover tooltip uses a `mapboxgl.Popup` attached to the marker's DOM element
 * — same pattern the historical LocationPicker used for saved-location pins.
 *
 * Phase 4 (#325) additions for the display-map migration:
 *  - `popup` (ReactNode) + `popupOpen` (bool) — controlled rich popup,
 *    rendered into a `mapboxgl.Popup` whose content is a host div we
 *    portal React into.
 *  - `iconElement` (ReactNode) — render a custom marker icon instead of the
 *    default colored pin. Used by LiveTracking's truck emoji marker.
 *  - `zIndex` + `scale` — visual emphasis for selected markers.
 */

import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type * as React from 'react';
import mapboxgl from 'mapbox-gl';
import type { MarkerProps } from '../../types';
import { useMapboxMap } from './MapboxMapView';

export const MapboxMarker: React.FC<MarkerProps> = ({
  position,
  color = '#f97316',
  draggable = false,
  onDragStart,
  onDrag,
  onDragEnd,
  onClick,
  onMouseEnter,
  onMouseLeave,
  tooltip,
  popup,
  popupOpen,
  iconElement,
  zIndex,
  scale,
}) => {
  const { map, ready } = useMapboxMap();
  const markerRef = useRef<mapboxgl.Marker | null>(null);
  // Two popup refs: one for hover-only tooltip (transient), one for the
  // controlled rich popup (persistent until closed). Keeping them separate
  // so a marker with both `tooltip` AND `popup` doesn't have its tooltip
  // overwrite the controlled popup's ref slot.
  const tooltipPopupRef = useRef<mapboxgl.Popup | null>(null);
  const popupRef = useRef<mapboxgl.Popup | null>(null);
  // Host element for the rich popup — we keep one detached <div> and let
  // mapbox-gl move it in/out of the DOM as the popup opens/closes. React
  // renders `popup` into this host via portal so consumers can pass JSX.
  const popupHostRef = useRef<HTMLDivElement | null>(null);
  if (!popupHostRef.current && typeof document !== 'undefined') {
    popupHostRef.current = document.createElement('div');
  }
  // Host element for a custom marker icon. Like the popup host, we keep one
  // div and portal the consumer's React content into it; mapboxgl uses the
  // host as the marker's `element` so it owns DOM placement.
  const iconHostRef = useRef<HTMLDivElement | null>(null);
  if (iconElement && !iconHostRef.current && typeof document !== 'undefined') {
    iconHostRef.current = document.createElement('div');
  }
  // Re-render trigger when the marker is constructed (so the portal can pick
  // up the host element). Without this, the portal may render before the
  // host is in the DOM.
  const [, setTick] = useState(0);

  // Stable handler refs — recreating the marker on every prop change is
  // expensive and visually flickers.
  const onDragStartRef = useRef(onDragStart);
  const onDragRef = useRef(onDrag);
  const onDragEndRef = useRef(onDragEnd);
  const onClickRef = useRef(onClick);
  const onMouseEnterRef = useRef(onMouseEnter);
  const onMouseLeaveRef = useRef(onMouseLeave);

  useEffect(() => { onDragStartRef.current = onDragStart; }, [onDragStart]);
  useEffect(() => { onDragRef.current = onDrag; }, [onDrag]);
  useEffect(() => { onDragEndRef.current = onDragEnd; }, [onDragEnd]);
  useEffect(() => { onClickRef.current = onClick; }, [onClick]);
  useEffect(() => { onMouseEnterRef.current = onMouseEnter; }, [onMouseEnter]);
  useEffect(() => { onMouseLeaveRef.current = onMouseLeave; }, [onMouseLeave]);

  // Mount / re-mount on color / draggable / iconElement-presence changes
  // (mapboxgl doesn't expose setters for any of these — recreating is the
  // only path).
  const hasIcon = !!iconElement;
  useEffect(() => {
    if (!map || !ready) return;
    const markerOpts: mapboxgl.MarkerOptions = hasIcon
      ? { element: iconHostRef.current!, draggable }
      : { color, draggable };
    const marker = new mapboxgl.Marker(markerOpts)
      .setLngLat([position.lng, position.lat])
      .addTo(map);
    markerRef.current = marker;
    setTick((t) => t + 1);

    marker.on('dragstart', () => onDragStartRef.current?.());
    marker.on('drag', () => {
      const ll = marker.getLngLat();
      onDragRef.current?.({ lat: ll.lat, lng: ll.lng });
    });
    marker.on('dragend', () => {
      const ll = marker.getLngLat();
      onDragEndRef.current?.({ lat: ll.lat, lng: ll.lng });
    });

    const el = marker.getElement();
    if (onClickRef.current) {
      el.style.cursor = 'pointer';
      el.addEventListener('click', handleClick);
    }
    el.addEventListener('mouseenter', handleEnter);
    el.addEventListener('mouseleave', handleLeave);

    return () => {
      el.removeEventListener('click', handleClick);
      el.removeEventListener('mouseenter', handleEnter);
      el.removeEventListener('mouseleave', handleLeave);
      tooltipPopupRef.current?.remove();
      tooltipPopupRef.current = null;
      popupRef.current?.remove();
      popupRef.current = null;
      marker.remove();
      markerRef.current = null;
    };
    function handleClick(e: MouseEvent) {
      e.stopPropagation();
      onClickRef.current?.();
    }
    function handleEnter() {
      if (tooltip && map) {
        const tp = new mapboxgl.Popup({ offset: 24, closeButton: false, closeOnClick: true })
          .setText(tooltip)
          .setLngLat(marker.getLngLat())
          .addTo(map);
        tooltipPopupRef.current = tp;
      }
      onMouseEnterRef.current?.();
    }
    function handleLeave() {
      tooltipPopupRef.current?.remove();
      tooltipPopupRef.current = null;
      onMouseLeaveRef.current?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, ready, color, draggable, hasIcon, tooltip]);

  // Controlled rich popup. Attached to the marker so it tracks position; we
  // open/close it based on popupOpen.
  useEffect(() => {
    const marker = markerRef.current;
    if (!map || !ready || !marker || !popup) return;

    // Lazily create the controlled popup once per marker mount. The host
    // element is the React portal target — mapboxgl owns its placement.
    let controlled = popupRef.current;
    if (!controlled) {
      controlled = new mapboxgl.Popup({
        offset: 25,
        closeButton: false,
        closeOnClick: false,
        maxWidth: '320px',
      }).setDOMContent(popupHostRef.current!);
      marker.setPopup(controlled);
      popupRef.current = controlled;
    }

    if (popupOpen) {
      if (!controlled.isOpen()) controlled.addTo(map);
    } else if (popupOpen === false) {
      if (controlled.isOpen()) controlled.remove();
    }
    // popupOpen === undefined → leave as-is (click/hover behavior decides).
  }, [map, ready, popup, popupOpen]);

  // Apply zIndex + scale on the marker element. Re-applied any time the
  // values change (or the marker remounts and trips the tick).
  useEffect(() => {
    const m = markerRef.current;
    if (!m) return;
    const el = m.getElement();
    if (zIndex !== undefined) el.style.zIndex = String(zIndex);
    else el.style.zIndex = '';
    if (scale !== undefined && scale !== 1) {
      el.style.transformOrigin = 'bottom center';
      // mapbox-gl owns the marker's `transform` for positioning. Wrap our
      // scale on top by writing through a CSS variable + leaving its
      // translate untouched. Simpler: append scale via filter? No — filter
      // doesn't change size. Use a wrapper style attribute that mapbox
      // re-applies positioning to and we re-apply scale to.
      // mapbox-gl rewrites `transform: translate(...)` every render frame,
      // so any `scale(...)` we append gets stomped. Workaround: write
      // `--twm-marker-scale` and have the inner content (the SVG / icon
      // element) read from it.
      el.style.setProperty('--twm-marker-scale', String(scale));
      // Apply to first child (the actual pin SVG / iconElement). Idempotent.
      const child = el.firstElementChild as HTMLElement | null;
      if (child) {
        child.style.transform = `scale(${scale})`;
        child.style.transformOrigin = 'bottom center';
      }
    } else {
      el.style.removeProperty('--twm-marker-scale');
      const child = el.firstElementChild as HTMLElement | null;
      if (child) {
        child.style.transform = '';
        child.style.transformOrigin = '';
      }
    }
  }, [zIndex, scale]);

  // Cheap position sync — `setLngLat` doesn't tear down the marker DOM, so
  // dragging-and-then-re-syncing-from-parent doesn't flicker.
  useEffect(() => {
    const m = markerRef.current;
    if (!m) return;
    const current = m.getLngLat();
    if (current.lng !== position.lng || current.lat !== position.lat) {
      m.setLngLat([position.lng, position.lat]);
    }
  }, [position.lat, position.lng]);

  // Portal the rich popup content into the host div (whether or not the
  // popup is currently visible — the host lives detached when closed).
  const popupPortal =
    popup && popupHostRef.current ? createPortal(popup, popupHostRef.current) : null;

  // Portal the custom icon content into the marker's element host.
  const iconPortal =
    iconElement && iconHostRef.current ? createPortal(iconElement, iconHostRef.current) : null;

  return (
    <>
      {popupPortal}
      {iconPortal}
    </>
  );
};
